/**
 * Tenant-scoped role constants.
 * These match tenant membership roles issued in the JWT `tenantRoles` array.
 */
export const TENANT_ROLES = {
  OWNER: 'OWNER',
  EMPLOYEE: 'EMPLOYEE',
  CUSTOMER: 'CUSTOMER'
} as const;

export type TenantRoleType = (typeof TENANT_ROLES)[keyof typeof TENANT_ROLES];

/** Display order of tenant roles in pickers and lists. */
export const TENANT_ROLE_ORDER: readonly TenantRoleType[] = [TENANT_ROLES.OWNER, TENANT_ROLES.EMPLOYEE, TENANT_ROLES.CUSTOMER];

/** Roles a user can hold inside a company; customers belong to the tenant only. */
export const COMPANY_ROLES: readonly TenantRoleType[] = [TENANT_ROLES.OWNER, TENANT_ROLES.EMPLOYEE];

/** Roles of tenant staff (shown in the Employees list, as opposed to customers). */
export const STAFF_TENANT_ROLES: readonly TenantRoleType[] = [TENANT_ROLES.OWNER, TENANT_ROLES.EMPLOYEE];

/** antd Tag preset colour per tenant role, shared by every role tag and editor. */
export const TENANT_ROLE_COLORS: Record<TenantRoleType, string> = {
  OWNER: 'purple',
  EMPLOYEE: 'blue',
  CUSTOMER: 'green'
};

/**
 * Type guard for tenant role values coming from the API (which types them as plain strings).
 * @param value - Any value.
 */
export const isTenantRole = (value: unknown): value is TenantRoleType =>
  typeof value === 'string' && (TENANT_ROLE_ORDER as readonly string[]).includes(value);
